import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { FileText, FileSpreadsheet, FileDown } from 'lucide-react';

interface ExportOptionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (format: 'pdf' | 'excel' | 'csv', options?: ExportOptions) => void;
}

interface ExportOptions {
  includeImages: boolean;
  includeComments: boolean;
  includeExceptions: boolean;
}

export function ExportOptionsDialog({ open, onOpenChange, onExport }: ExportOptionsDialogProps) {
  const [format, setFormat] = useState<'pdf' | 'excel' | 'csv'>('pdf');
  const [options, setOptions] = useState<ExportOptions>({
    includeImages: true,
    includeComments: true,
    includeExceptions: true,
  });

  const handleExport = () => {
    onExport(format, options);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Export Audit Report</DialogTitle>
          <DialogDescription>
            Choose the export format and options for your audit report.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-6 py-4">
          <div className="grid gap-3">
            <Label>Export Format</Label>
            <RadioGroup value={format} onValueChange={(value: string) => setFormat(value as 'pdf' | 'excel' | 'csv')}>
              <div className="flex items-center space-x-3 rounded-lg border p-4 hover:bg-accent cursor-pointer">
                <RadioGroupItem value="pdf" id="pdf" />
                <Label htmlFor="pdf" className="flex items-center gap-3 cursor-pointer flex-1">
                  <FileText className="h-5 w-5 text-red-500" />
                  <div>
                    <div className="font-medium">PDF Document</div>
                    <div className="text-sm text-muted-foreground">
                      Formatted report with images and charts
                    </div>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-center space-x-3 rounded-lg border p-4 hover:bg-accent cursor-pointer">
                <RadioGroupItem value="excel" id="excel" />
                <Label htmlFor="excel" className="flex items-center gap-3 cursor-pointer flex-1">
                  <FileSpreadsheet className="h-5 w-5 text-green-500" />
                  <div>
                    <div className="font-medium">Excel Spreadsheet</div>
                    <div className="text-sm text-muted-foreground">
                      Detailed data in spreadsheet format
                    </div>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-center space-x-3 rounded-lg border p-4 hover:bg-accent cursor-pointer">
                <RadioGroupItem value="csv" id="csv" />
                <Label htmlFor="csv" className="flex items-center gap-3 cursor-pointer flex-1">
                  <FileDown className="h-5 w-5 text-blue-500" />
                  <div>
                    <div className="font-medium">CSV File</div>
                    <div className="text-sm text-muted-foreground">
                      Raw data for further processing
                    </div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="grid gap-3">
            <Label>Include in Export</Label>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="images"
                  checked={options.includeImages}
                  onCheckedChange={(checked) =>
                    setOptions({ ...options, includeImages: checked as boolean })
                  }
                  disabled={format === 'csv'}
                />
                <Label
                  htmlFor="images"
                  className={cn(
                    'text-sm font-normal cursor-pointer',
                    format === 'csv' && 'text-muted-foreground'
                  )}
                >
                  Asset images and photos
                </Label>
              </div>
              
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="comments"
                  checked={options.includeComments}
                  onCheckedChange={(checked) =>
                    setOptions({ ...options, includeComments: checked as boolean })
                  }
                />
                <Label htmlFor="comments" className="text-sm font-normal cursor-pointer">
                  Comments and notes
                </Label>
              </div>
              
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="exceptions"
                  checked={options.includeExceptions}
                  onCheckedChange={(checked) =>
                    setOptions({ ...options, includeExceptions: checked as boolean })
                  }
                />
                <Label htmlFor="exceptions" className="text-sm font-normal cursor-pointer">
                  Exceptions and issues
                </Label>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleExport}>
            <FileDown className="mr-2 h-4 w-4" />
            Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function cn(...classes: (string | boolean | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}
